import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useSuperAdmin } from '../../context/SuperAdminContext';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Checkbox } from '../../components/ui/checkbox';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  Eye,
  Rocket,
  AlertTriangle,
  Globe,
  ChevronRight,
  Loader2,
  CalendarIcon,
  X,
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import RichTextEditor from '../../components/RichTextEditor';

const TYPE_META = {
  release_notes: { label: 'Release Notes', icon: Rocket, color: 'bg-blue-500/20 text-blue-400' },
  announcement: { label: 'Announcement', icon: Megaphone, color: 'bg-[#ffd108]/20 text-[#ffd108]' },
  maintenance: { label: 'Maintenance', icon: AlertTriangle, color: 'bg-amber-500/20 text-amber-400' },
};

const DISPLAY_META = {
  banner: { label: 'Banner' },
  popup: { label: 'Popup' },
  both: { label: 'Both' },
};

const EMPTY_FORM = {
  title: '',
  body: '',
  type: 'announcement',
  display_mode: 'popup',
  target_audience: { type: 'all', values: [] },
  published: false,
  active_from: null,
  active_until: null,
};

const TIER_OPTIONS = [
  { value: 'basic', label: 'Basic' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'professional', label: 'Professional' },
];

const Announcements = () => {
  const { api } = useSuperAdmin();
  const { isDark = true } = useOutletContext() || {};
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [tenantsList, setTenantsList] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);

  const fetchItems = async () => {
    try {
      const res = await api.get('/api/super-admin/announcements');
      setItems(res.data);
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const res = await api.get('/api/super-admin/tenants-list');
      setTenantsList((res.data || []).map(t => ({ value: t.client_id, label: t.company_name || t.client_id, tier: t.tier || 'basic' })));
    } catch (err) {
      console.error('Failed to fetch tenants:', err);
    }
  };

  useEffect(() => { fetchItems(); fetchTenants(); }, [api]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      body: item.body,
      type: item.type,
      display_mode: item.display_mode,
      target_audience: item.target_audience || { type: 'all', values: [] },
      published: item.published,
      active_from: item.active_from ? new Date(item.active_from) : null,
      active_until: item.active_until ? new Date(item.active_until) : null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.body.trim() || form.body === '<p></p>') { toast.error('Content is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        active_from: form.active_from instanceof Date ? form.active_from.toISOString() : null,
        active_until: form.active_until instanceof Date ? form.active_until.toISOString() : null,
      };

      if (editingId) {
        await api.put(`/api/super-admin/announcements/${editingId}`, payload);
        toast.success('Announcement updated');
      } else {
        await api.post('/api/super-admin/announcements', payload);
        toast.success('Announcement created');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/api/super-admin/announcements/${deleteId}`);
      toast.success('Announcement deleted');
      setDeleteId(null);
      fetchItems();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const togglePublished = async (item) => {
    try {
      await api.put(`/api/super-admin/announcements/${item.id}`, { published: !item.published });
      toast.success(item.published ? 'Unpublished' : 'Published');
      fetchItems();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
            <Megaphone className="h-6 w-6 text-[#ffd108]" />
            Announcements
          </h1>
          <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>
            {items.length} announcement{items.length !== 1 ? 's' : ''} — publish release notes, updates, and maintenance notices
          </p>
        </div>
        <Button onClick={openCreate} className="bg-[#ffd108] text-black hover:bg-[#e6bc07]" data-testid="create-announcement-btn">
          <Plus className="h-4 w-4 mr-2" /> New Announcement
        </Button>
      </div>

      {/* Table */}
      <Card className={isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200 shadow-sm'}>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <div className="text-center py-16">
              <Megaphone className={`h-12 w-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
              <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No announcements yet</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Create your first announcement to notify users</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={isDark ? 'border-slate-700 hover:bg-transparent' : 'border-gray-200 hover:bg-transparent'}>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Title</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Type</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Display</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Audience</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Status</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}>Created</TableHead>
                  <TableHead className={isDark ? 'text-slate-400' : 'text-gray-500'}></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => {
                  const typeMeta = TYPE_META[item.type] || TYPE_META.announcement;
                  const TypeIcon = typeMeta.icon;
                  return (
                    <TableRow
                      key={item.id}
                      className={isDark ? 'border-slate-700 hover:bg-slate-700/30' : 'border-gray-100 hover:bg-gray-50'}
                      data-testid={`announcement-row-${item.id}`}
                    >
                      <TableCell>
                        <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{item.title}</p>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeMeta.color}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {typeMeta.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className={`text-sm capitalize ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                          {DISPLAY_META[item.display_mode]?.label || item.display_mode}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Globe className={`h-3 w-3 ${isDark ? 'text-slate-400' : 'text-gray-400'}`} />
                          <span className={`text-sm capitalize ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
                            {item.target_audience?.type === 'all' ? 'All' : item.target_audience?.type === 'tiers'
                              ? item.target_audience.values.join(', ')
                              : `${item.target_audience?.values?.length || 0} tenant(s)`}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={item.published ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'}>
                          {item.published ? 'Published' : 'Draft'}
                        </Badge>
                      </TableCell>
                      <TableCell className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                        {item.created_at ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true }) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400'}
                            onClick={() => { setPreviewItem(item); setPreviewOpen(true); }} data-testid={`preview-${item.id}`}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className={isDark ? 'text-slate-400 hover:text-white' : 'text-gray-400'}
                            onClick={() => openEdit(item)} data-testid={`edit-${item.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300"
                            onClick={() => setDeleteId(item.id)} data-testid={`delete-${item.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`sm:max-w-[750px] max-h-[90vh] overflow-y-auto ${isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>
              {editingId ? 'Edit Announcement' : 'New Announcement'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Title <span className="text-red-500">*</span></Label>
              <Input
                value={form.title}
                onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. v2.4 Release Notes — Haulage & Docket Updates"
                className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''}
                data-testid="announcement-title-input"
                maxLength={200}
              />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm(p => ({ ...p, type: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''} data-testid="announcement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="release_notes">Release Notes</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Display Mode</Label>
                <Select value={form.display_mode} onValueChange={(v) => setForm(p => ({ ...p, display_mode: v }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''} data-testid="announcement-display-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="popup">Popup (on login)</SelectItem>
                    <SelectItem value="banner">Banner (top bar)</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Audience</Label>
                <Select value={form.target_audience.type} onValueChange={(v) => setForm(p => ({ ...p, target_audience: { type: v, values: [] } }))}>
                  <SelectTrigger className={isDark ? 'bg-slate-800 border-slate-600 text-white' : ''} data-testid="announcement-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenants</SelectItem>
                    <SelectItem value="tiers">Specific Tiers</SelectItem>
                    <SelectItem value="tenants">Specific Tenants</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {form.target_audience.type === 'tiers' && (
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Select Tiers</Label>
                <div className={`rounded-lg border p-3 space-y-2 ${isDark ? 'bg-slate-800/60 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                  {TIER_OPTIONS.map((tier) => (
                    <label key={tier.value} className="flex items-center gap-2.5 cursor-pointer">
                      <Checkbox
                        checked={form.target_audience.values.includes(tier.value)}
                        onCheckedChange={(checked) => {
                          setForm(p => ({
                            ...p,
                            target_audience: {
                              ...p.target_audience,
                              values: checked
                                ? [...p.target_audience.values, tier.value]
                                : p.target_audience.values.filter(v => v !== tier.value),
                            },
                          }));
                        }}
                        className="data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108]"
                        data-testid={`tier-checkbox-${tier.value}`}
                      />
                      <span className={`text-sm ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{tier.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.target_audience.type === 'tenants' && (
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Select Tenants</Label>
                {/* Selected tenant chips */}
                {form.target_audience.values.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {form.target_audience.values.map((v) => {
                      const tenant = tenantsList.find(t => t.value === v);
                      return (
                        <Badge key={v} className="bg-[#ffd108]/15 text-[#ffd108] border-[#ffd108]/30 text-xs gap-1 py-1">
                          {tenant ? tenant.label : v}
                          <button
                            type="button"
                            onClick={() => setForm(p => ({
                              ...p,
                              target_audience: { ...p.target_audience, values: p.target_audience.values.filter(x => x !== v) },
                            }))}
                            className="hover:text-white ml-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      );
                    })}
                  </div>
                )}
                {/* Dropdown multi-select */}
                <Button
                  type="button"
                  variant="outline"
                  className={`w-full justify-start text-left font-normal ${
                    isDark ? 'bg-slate-800 border-slate-600 text-white hover:bg-slate-700' : 'bg-gray-50 border-gray-200 text-gray-900'
                  } ${form.target_audience.values.length === 0 ? (isDark ? 'text-slate-500' : 'text-gray-400') : ''}`}
                  onClick={() => setTenantDropdownOpen(prev => !prev)}
                  data-testid="tenant-multi-select-trigger"
                >
                  <Globe className="mr-2 h-4 w-4" />
                  {form.target_audience.values.length === 0
                    ? 'Select tenants...'
                    : `${form.target_audience.values.length} tenant${form.target_audience.values.length !== 1 ? 's' : ''} selected`}
                  <ChevronRight className={`ml-auto h-4 w-4 transition-transform ${tenantDropdownOpen ? 'rotate-90' : ''}`} />
                </Button>
                {tenantDropdownOpen && (
                  <div className={`rounded-lg border ${isDark ? 'bg-slate-800/80 border-slate-600' : 'bg-white border-gray-200'}`}>
                    {/* Search */}
                    <div className={`p-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                      <Input
                        placeholder="Search tenants..."
                        value={tenantSearch}
                        onChange={(e) => setTenantSearch(e.target.value)}
                        className={`h-8 ${isDark ? 'bg-slate-900 border-slate-600 text-white' : ''}`}
                        data-testid="tenant-search-input"
                      />
                    </div>
                    {/* Tenant list */}
                    <div className="max-h-[200px] overflow-y-auto p-1">
                      {tenantsList.length === 0 ? (
                        <div className="flex items-center justify-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin text-slate-400 mr-2" />
                          <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Loading tenants...</span>
                        </div>
                      ) : (
                        (() => {
                          const q = tenantSearch.toLowerCase();
                          const filtered = tenantsList.filter(t =>
                            t.label.toLowerCase().includes(q) || t.value.toLowerCase().includes(q)
                          );
                          if (filtered.length === 0) {
                            return <p className={`text-sm text-center py-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>No tenants match "{tenantSearch}"</p>;
                          }
                          return filtered.map((t) => {
                            const isSelected = form.target_audience.values.includes(t.value);
                            return (
                              <div
                                key={t.value}
                                className={`flex items-center gap-2.5 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                                  isSelected
                                    ? isDark ? 'bg-[#ffd108]/10' : 'bg-yellow-50'
                                    : isDark ? 'hover:bg-slate-700' : 'hover:bg-gray-100'
                                }`}
                                onClick={() => {
                                  setForm(p => ({
                                    ...p,
                                    target_audience: {
                                      ...p.target_audience,
                                      values: isSelected
                                        ? p.target_audience.values.filter(v => v !== t.value)
                                        : [...p.target_audience.values, t.value],
                                    },
                                  }));
                                }}
                                data-testid={`tenant-option-${t.value}`}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  className="data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108] pointer-events-none"
                                />
                                <div className="flex-1 min-w-0">
                                  <span className={`text-sm ${isDark ? 'text-slate-200' : 'text-gray-700'}`}>{t.label}</span>
                                  <span className={`text-xs ml-1.5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{t.value}</span>
                                </div>
                                <Badge className={`text-[10px] ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>{t.tier}</Badge>
                              </div>
                            );
                          });
                        })()
                      )}
                    </div>
                    {/* Footer with count */}
                    {form.target_audience.values.length > 0 && (
                      <div className={`p-2 border-t ${isDark ? 'border-slate-700' : 'border-gray-200'} flex items-center justify-between`}>
                        <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                          {form.target_audience.values.length} selected
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs text-red-400 hover:text-red-300"
                          onClick={() => setForm(p => ({ ...p, target_audience: { ...p.target_audience, values: [] } }))}
                        >
                          Clear all
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Active From (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        isDark ? 'bg-slate-800 border-slate-600 text-white hover:bg-slate-700' : 'bg-gray-50 border-gray-200 text-gray-900'
                      } ${!form.active_from ? (isDark ? 'text-slate-500' : 'text-gray-400') : ''}`}
                      data-testid="active-from-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.active_from ? format(form.active_from, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.active_from || undefined}
                      onSelect={(date) => setForm(p => ({ ...p, active_from: date || null }))}
                    />
                    {form.active_from && (
                      <div className="p-2 border-t">
                        <Button variant="ghost" size="sm" className="w-full text-red-400"
                          onClick={() => setForm(p => ({ ...p, active_from: null }))}>
                          <X className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1.5">
                <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Active Until (optional)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={`w-full justify-start text-left font-normal ${
                        isDark ? 'bg-slate-800 border-slate-600 text-white hover:bg-slate-700' : 'bg-gray-50 border-gray-200 text-gray-900'
                      } ${!form.active_until ? (isDark ? 'text-slate-500' : 'text-gray-400') : ''}`}
                      data-testid="active-until-picker"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {form.active_until ? format(form.active_until, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={form.active_until || undefined}
                      onSelect={(date) => setForm(p => ({ ...p, active_until: date || null }))}
                    />
                    {form.active_until && (
                      <div className="p-2 border-t">
                        <Button variant="ghost" size="sm" className="w-full text-red-400"
                          onClick={() => setForm(p => ({ ...p, active_until: null }))}>
                          <X className="h-3 w-3 mr-1" /> Clear
                        </Button>
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>Content <span className="text-red-500">*</span></Label>
              <RichTextEditor
                content={form.body}
                onChange={(html) => setForm(p => ({ ...p, body: html }))}
                isDark={isDark}
                placeholder="Write your announcement content..."
              />
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm(p => ({ ...p, published: v }))}
                data-testid="announcement-published-switch"
              />
              <Label className={isDark ? 'text-slate-300' : 'text-gray-700'}>
                {form.published ? 'Published (visible to users)' : 'Draft (not visible)'}
              </Label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className={isDark ? 'border-slate-600 text-slate-300' : ''}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#ffd108] text-black hover:bg-[#e6bc07]" data-testid="announcement-save-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {editingId ? 'Save Changes' : 'Create Announcement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`sm:max-w-[600px] max-h-[80vh] overflow-y-auto ${isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-gray-200'}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>{previewItem?.title}</DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className={TYPE_META[previewItem.type]?.color}>
                  {TYPE_META[previewItem.type]?.label}
                </Badge>
                {previewItem.created_at && (
                  <span className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                    {format(new Date(previewItem.created_at), 'MMM d, yyyy')}
                  </span>
                )}
              </div>
              <div
                className={`prose prose-sm max-w-none ${isDark ? 'prose-invert' : ''}`}
                dangerouslySetInnerHTML={{ __html: previewItem.body }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent className={isDark ? 'bg-slate-900 border-slate-700' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={isDark ? 'text-white' : ''}>Delete Announcement?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone. Users will no longer see this announcement.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={isDark ? 'border-slate-600 text-slate-300' : ''}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Announcements;
